import React, {useContext, useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {reaction} from 'mobx';
import {useLocation} from 'react-router-dom';
import {Context} from '../../../index';
import {getAuthCredentialGeneration, subscribeAuthTokenCommit} from '../../../http/siteAuthTokenCommit';
import {getIncomingTarget, getIncomingCurrent, prepareIncomingCommand, discoverIncomingCommands,
  getIncomingCommand, confirmIncomingCommand, reviseIncomingCommand, recoverIncomingCommand} from '../../../http/preorderIncomingProfilesApi';
import {normalizeIncomingCommand, moscowCalendarToUtc, inspectPending, savePending, promotePending,
  clearPending, mergeIncomingUncertainty} from '../../../utils/adminPreorderIncomingProfiles';
import './AdminPreorderIncoming.css';

// Scheduling only: no command, receipt, token or business authority survives in
// this module. An entered actor lane stays owned until its promise settles,
// including credential changes, route departure and component unmount.
const activeLanes = new Map();
const laneListeners = new Set();
const publishLanes = () => {for (const listener of laneListeners) listener();};
const HANDLE_VERSION = 'confirmed-preorder-incoming-admin-pending-v1';
const MAX_ID = 2147483647;
const MODE_LABELS = {initial: 'Начальное подтверждение', preparation: 'Подготовка правил и подтверждение', revision: 'Изменение подтверждённой поставки'};
const emptyTerms = () => ({capacity: '', reference: '', from: '', to: '', payNow: false, payLater: false});
const emptyView = () => ({actorId: null, route: '', credential: null, child: '', size: '', mode: 'initial', terms: emptyTerms(),
  target: null, current: null, page: null, pageQuery: null, limit: '10', saved: null, receipt: null, evidence: null,
  handle: null, pendingState: 'none', blocked: false, reviewReady: false, submitAllowed: false, checked: false,
  needsFreshCurrent: false, flags: {outcomeUnknown: false, recoveryRequired: false}, error: null, status: ''});
const inputId = text => typeof text === 'string' && /^[1-9]\d*$/.test(text) &&
  Number.isSafeInteger(Number(text)) && Number(text) <= MAX_ID ? Number(text) : null;
const sameHandle = (left, right) => Boolean(left && right && ['schemaVersion', 'actorId', 'operationId', 'commandSha256']
  .every(key => left[key] === right[key]));
const storage = () => {try {return window.sessionStorage;} catch (_) {return null;}};
const localError = (message, field = null) => Object.assign(new Error('Проверьте данные поставки.'),
  {kind: 'input', status: null, outcomeUnknown: false, recoveryRequired: false, displayMessage: message, field});
const errorData = (error, key) => {
  if (!error || typeof error !== 'object') return undefined;
  const descriptor = Object.getOwnPropertyDescriptor(error, key);
  return descriptor && Object.prototype.hasOwnProperty.call(descriptor, 'value') ? descriptor.value : undefined;
};
const errorText = error => {
  const local = errorData(error, 'displayMessage');
  if (typeof local === 'string' && local.length <= 200) return local;
  const status = errorData(error, 'status');
  if (status === 401 || status === 403) return 'Доступ к операции отклонён. Проверьте действующий доступ администратора.';
  if (status === 409) return 'Условия операции конфликтуют с данными сервера. Проверьте результат этой же операции.';
  if (status === 429) return 'Дождитесь завершения запроса и вручную повторите проверку этой же операции.';
  return errorData(error, 'kind') === 'input' ? 'Проверьте заполненные условия поставки.' : 'Не удалось проверить операцию поставки. Повторите проверку вручную.';
};
const newOperationId = () => {
  try {
    const crypto = window.crypto;
    if (!crypto || typeof crypto.getRandomValues !== 'function') throw new Error();
    const bytes = new Uint8Array(16); crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
    return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
  } catch (_) {throw localError('В этом браузере недоступно безопасное создание операции.');}
};
const calendarReady = terms => {
  try {return moscowCalendarToUtc(terms.from) <= moscowCalendarToUtc(terms.to);} catch (_) {return false;}
};
const moscowTime = iso => new Date(new Date(iso).getTime() + 10800000).toISOString().slice(0, 16).replace('T', ' ');
const referenceFor = view => {
  const current = view.current, evidence = view.evidence;
  if (!current?.batch || !evidence || evidence.receipt.batchId !== current.batch.batchId) return null;
  for (const key of ['productId', 'childProductId', 'colorId', 'sizeId']) if (evidence.receipt[key] !== current.identity[key]) return null;
  return evidence.command.confirmationRef === evidence.receipt.confirmationRef ? evidence.command.confirmationRef : null;
};
const compatibleMode = view => view.current && !view.needsFreshCurrent && (
  view.mode === 'initial' && view.current.readState === 'policy_unlinked' ||
  view.mode === 'preparation' && ['policy_absent', 'policy_unlinked'].includes(view.current.readState) ||
  view.mode === 'revision' && view.current.readState === 'batch_linked' && view.current.batch.state === 'accepting' && referenceFor(view));
const termsReady = view => {
  const reference = view.mode === 'revision' ? referenceFor(view) : view.terms.reference;
  const capacity = inputId(view.terms.capacity);
  return Boolean(compatibleMode(view) && capacity && typeof reference === 'string' && /\S/.test(reference) &&
    calendarReady(view.terms) && (view.terms.payNow || view.terms.payLater) &&
    (view.mode !== 'revision' || capacity >= view.current.batch.accountedQuantity));
};
const originalMatches = view => Boolean(view.saved && view.handle && view.handle.commandSha256 &&
  view.saved.operationId === view.handle.operationId && view.saved.commandSha256 === view.handle.commandSha256);

const OriginalTerms = ({saved}) => <>
  <p>Режим: {MODE_LABELS[saved.operation]}</p><p>ID операции: {saved.operationId}</p><p>{saved.commandSha256}</p>
  <p>SKU: {saved.command.productId} / {saved.command.childProductId} / {saved.command.colorId} / {saved.command.sizeId}</p>
  <p>{saved.command.confirmationRef}</p><p>Всего единиц в подтверждённой поставке: {saved.command.capacity}</p>
  <p>Срок отправки: {moscowTime(saved.command.dispatchEta.from)} — {moscowTime(saved.command.dispatchEta.to)} (Москва)</p>
  <p>Оплата сейчас: {saved.command.payNowAllowed ? 'разрешена' : 'не разрешена'}; оплата позже: {saved.command.payLaterAllowed ? 'разрешена' : 'не разрешена'}.</p>
  <p>{saved.command.expectedPolicyRevision === null ? 'Правила отсутствовали.' : 'Версия правил: ' + saved.command.expectedPolicyRevision}</p>
  {saved.operation === 'revision' && <><p>ID партии: {saved.command.batchId}</p><p>Версия поставки: {saved.command.expectedLedgerRevision}</p></>}
  <p>Сохранение команды не подтверждает поставку. Условия исходной команды доступны только для чтения.</p>
</>;
const CurrentDiagnostics = ({current}) => current ? <>
  <p>SKU: {current.identity.productId} / {current.identity.childProductId} / {current.identity.colorId} / {current.identity.sizeId}; размер {current.identity.sizeName}</p>
  <p>{current.readState === 'policy_absent' ? 'Правила отсутствуют.' : current.readState === 'policy_unlinked' ? 'Поставка не связана.' : 'Подтверждённая поставка связана с правилами.'}</p>
  {current.policy && <p>Версия правил: {current.policy.policyRevision}</p>}
  {current.batch && <><p>ID партии: {current.batch.batchId}</p><p>Версия поставки: {current.batch.ledgerRevision}</p>
    <p>Всего единиц в подтверждённой поставке: {current.batch.capacity}</p><p>Уже принято по предзаказам: {current.batch.accountedQuantity}</p>
    <p>Срок отправки: {moscowTime(current.batch.dispatchEta.from)} — {moscowTime(current.batch.dispatchEta.to)} (Москва)</p></>}
  <p>Последняя ручная проверка; эти данные не разрешают публичное оформление заказа.</p>
</> : <p>Текущие данные не загружены.</p>;
const HistoricalReceipt = ({receipt}) => receipt ? <>
  <p>Поставка подтверждена.</p><p>Исторический результат — не текущая доступность.</p>
  <p>SKU: {receipt.productId} / {receipt.childProductId} / {receipt.colorId} / {receipt.sizeId}</p>
  <p>ID партии: {receipt.batchId}</p><p>Всего единиц в подтверждённой поставке: {receipt.capacity}</p>
  <p>Срок отправки: {moscowTime(receipt.dispatchEta.from)} — {moscowTime(receipt.dispatchEta.to)} (Москва)</p>
  <p>Версия правил: {receipt.policyRevision}; версия поставки: {receipt.ledgerRevision}</p>
  <p>Декларация ответственного сотрудника; внешняя проверка поставки не выполнена.</p>
</> : <p>Подтверждённый сервером исторический результат не загружен.</p>;

const AdminPreorderIncoming = observer(() => {
  const {user} = useContext(Context);
  const location = useLocation();
  const [view, setView] = useState(emptyView);
  const [, setLanePulse] = useState(0);
  const viewRef = useRef(null);
  const lifetime = useRef({alive: false, epoch: 0, route: ''});
  const errorRef = useRef(null), statusRef = useRef(null);
  const actorId = user.isAuth === true && user.user.role === 'ADMIN' && Number.isSafeInteger(user.user.id) &&
    user.user.id > 0 && user.user.id <= MAX_ID ? user.user.id : null;
  const credential = getAuthCredentialGeneration();
  const sessionCurrent = actorId !== null && view.actorId === actorId && view.route === location.pathname && view.credential === credential;
  const visible = sessionCurrent ? view : emptyView();
  const busy = actorId !== null && activeLanes.has(actorId);
  const admitted = sessionCurrent && !visible.blocked;
  const controls = admitted && !busy;
  const update = change => {
    if (!lifetime.current.alive) return;
    const previous = viewRef.current || emptyView();
    const next = typeof change === 'function' ? change(previous) : {...previous, ...change};
    viewRef.current = next; setView(next);
  };
  const principal = () => user.isAuth === true && user.user.role === 'ADMIN' && Number.isSafeInteger(user.user.id) &&
    user.user.id > 0 && user.user.id <= MAX_ID ? user.user.id : null;

  useEffect(() => {
    const owner = lifetime.current;
    owner.alive = true; owner.route = location.pathname;
    const reset = () => {
      owner.epoch++;
      const id = principal(), next = emptyView();
      next.actorId = id; next.route = owner.route; next.credential = getAuthCredentialGeneration();
      if (id !== null) {
        const inspected = inspectPending(storage(), id); next.pendingState = inspected.state;
        if (inspected.state === 'owned-valid') {
          next.handle = inspected.pending; next.flags = {outcomeUnknown: true, recoveryRequired: true};
        } else if (inspected.state !== 'none') {
          next.blocked = true; next.error = {message: 'Сохранённая операция недоступна или конфликтует с текущим сотрудником. Не заменяйте её.'};
        }
      }
      update(next); setLanePulse(value => value + 1);
    };
    reset();
    const stopPrincipal = reaction(() => [user.isAuth, user.user.id, user.user.role], reset);
    const stopCredential = subscribeAuthTokenCommit(reset);
    const laneChanged = () => {if (owner.alive) setLanePulse(value => value + 1);};
    laneListeners.add(laneChanged);
    return () => {
      owner.alive = false; owner.epoch++; viewRef.current = null;
      stopPrincipal(); stopCredential(); laneListeners.delete(laneChanged);
      // No abort, pending deletion, lane release or dispatched write on cleanup.
    };
    // Actual UserStore reactions and the credential subscription own invalidation.
    // Only a new user store or route creates a new component lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname]);
  useEffect(() => {
    if (!sessionCurrent) return;
    if (view.error) errorRef.current?.focus(); else if (view.status) statusRef.current?.focus();
  }, [view.error, view.status, sessionCurrent]);

  const validFrame = frame => lifetime.current.alive && lifetime.current.epoch === frame.epoch &&
    lifetime.current.route === frame.route && principal() === frame.actorId && getAuthCredentialGeneration() === frame.credential;
  const blockStorage = () => update({blocked: true, pendingState: 'conflict', checked: false, reviewReady: false, submitAllowed: false,
    error: {message: 'Сохранённая операция изменилась или недоступна. Ничего не заменено; продолжение заблокировано.'}});
  const checkHandle = expected => {
    const inspected = inspectPending(storage(), expected.actorId);
    if (inspected.state !== 'owned-valid' || !sameHandle(inspected.pending, expected)) {blockStorage(); return false;}
    return true;
  };
  const run = async (operation, work) => {
    const id = principal(), state = viewRef.current;
    if (!lifetime.current.alive || id === null || !state || state.blocked || state.actorId !== id ||
      state.route !== lifetime.current.route || state.credential !== getAuthCredentialGeneration() || activeLanes.has(id)) return;
    const frame = {actorId: id, credential: getAuthCredentialGeneration(), epoch: lifetime.current.epoch,
      route: lifetime.current.route, token: Symbol('incoming-ui-lane')};
    activeLanes.set(id, frame.token); publishLanes(); update({error: null, status: 'Ожидаем ответ сервера.'});
    try {await work(frame);} catch (error) {
      if (validFrame(frame)) {
        const previous = viewRef.current;
        const unknown = errorData(error, 'outcomeUnknown'), recovery = errorData(error, 'recoveryRequired');
        const fallback = !['target', 'current'].includes(operation);
        const flags = mergeIncomingUncertainty(previous.flags, {outcomeUnknown: typeof unknown === 'boolean' ? unknown : fallback,
          recoveryRequired: typeof recovery === 'boolean' ? recovery : fallback});
        const denied = [401, 403].includes(errorData(error, 'status'));
        update({...previous, flags, checked: false, submitAllowed: false, status: '',
          ...(operation === 'commands' ? {page: null, pageQuery: null} : {}),
          ...(denied ? {saved: null, receipt: null, evidence: null, target: null, current: null, reviewReady: false} : {}),
          error: {message: errorText(error), field: errorData(error, 'field') || null}});
      }
    } finally {
      if (activeLanes.get(id) === frame.token) activeLanes.delete(id);
      publishLanes();
    }
  };
  const changeDraft = patch => {
    lifetime.current.epoch++;
    update(previous => ({...previous, ...patch, checked: false, reviewReady: false, error: null, status: ''}));
  };
  const changeTerms = (key, value) => changeDraft({terms: {...viewRef.current.terms, [key]: value}});
  const handleTarget = () => run('target', async frame => {
    const state = viewRef.current, childProductId = inputId(state.child);
    if (!childProductId) throw localError('Укажите корректный ID варианта.', 'child');
    const result = await getIncomingTarget({childProductId}, {actorId: frame.actorId});
    if (validFrame(frame)) update({target: result, current: null, size: '', page: null, pageQuery: null,
      reviewReady: false, checked: false, status: 'Размеры загружены. Выберите размер и вручную проверьте текущие данные.'});
  });
  const handleCurrent = () => run('current', async frame => {
    const state = viewRef.current, childProductId = inputId(state.child), sizeId = inputId(state.size);
    if (!childProductId || !state.target?.candidates.some(candidate => candidate.sizeId === sizeId)) throw localError('Выберите размер из загруженного списка.', 'size');
    const result = await getIncomingCurrent({childProductId, sizeId}, {actorId: frame.actorId});
    if (validFrame(frame)) update({current: result, needsFreshCurrent: false, reviewReady: false, checked: false, status: 'Текущие данные проверены вручную.'});
  });
  const handlePrepare = () => {
    const state = viewRef.current;
    if (!state || state.handle || !termsReady(state)) return;
    return run('prepare', async frame => {
      const original = viewRef.current, inspected = inspectPending(storage(), frame.actorId);
      if (inspected.state !== 'none') {blockStorage(); return;}
      const operationId = newOperationId(), current = original.current;
      const command = normalizeIncomingCommand(original.mode, {idempotencyKey: operationId,
        productId: current.identity.productId, childProductId: current.identity.childProductId, colorId: current.identity.colorId, sizeId: current.identity.sizeId,
        expectedPolicyRevision: current.policy === null ? null : current.policy.policyRevision,
        confirmationRef: original.mode === 'revision' ? referenceFor(original) : original.terms.reference,
        capacity: inputId(original.terms.capacity), dispatchEta: {from: moscowCalendarToUtc(original.terms.from), to: moscowCalendarToUtc(original.terms.to)},
        payNowAllowed: original.terms.payNow, payLaterAllowed: original.terms.payLater,
        ...(original.mode === 'revision' ? {batchId: current.batch.batchId, expectedLedgerRevision: current.batch.ledgerRevision} : {})});
      const handle = Object.freeze({schemaVersion: HANDLE_VERSION, actorId: frame.actorId, operationId, commandSha256: null});
      if (!savePending(storage(), handle)) {blockStorage(); return;}
      update({handle, pendingState: 'owned-valid', saved: null, reviewReady: false, checked: false,
        submitAllowed: false, flags: {outcomeUnknown: false, recoveryRequired: false}});
      const saved = await prepareIncomingCommand({operation: original.mode, command}, {actorId: frame.actorId});
      if (!validFrame(frame)) return;
      if (!promotePending(storage(), handle, saved)) {blockStorage(); return;}
      update({handle: Object.freeze({...handle, commandSha256: saved.commandSha256}), saved, reviewReady: true, submitAllowed: true,
        checked: false, status: 'Исходная команда сохранена сервером. Проверьте её условия и подтвердите поставку вручную.'});
    });
  };
  const handleRead = row => run('command', async frame => {
    let handle = viewRef.current.handle;
    if (row && handle && (handle.operationId !== row.operationId || handle.commandSha256 !== null && handle.commandSha256 !== row.commandSha256)) return;
    if (!handle) {
      if (!row) return;
      handle = Object.freeze({schemaVersion: HANDLE_VERSION, actorId: frame.actorId, operationId: row.operationId, commandSha256: null});
      if (!savePending(storage(), handle)) {blockStorage(); return;}
      update({handle, pendingState: 'owned-valid', flags: {outcomeUnknown: true, recoveryRequired: true}, submitAllowed: false});
    }
    if (!checkHandle(handle)) return;
    const saved = await getIncomingCommand({operationId: handle.operationId, commandSha256: row ? row.commandSha256 : handle.commandSha256}, {actorId: frame.actorId});
    if (!validFrame(frame)) return;
    if (saved.registryState === 'not_found') {
      update({saved: null, reviewReady: false, checked: false, submitAllowed: false,
        flags: {outcomeUnknown: true, recoveryRequired: true}, status: 'Сохранённая команда не найдена. Исходные условия не подтверждены; операция сохранена для восстановления.'}); return;
    }
    // Both actual prepared ACK and actual found original are accepted by the
    // reviewed storage/API helpers. Never rewrite the server's registryState.
    if (handle.commandSha256 === null && !promotePending(storage(), handle, saved)) {blockStorage(); return;}
    const promoted = Object.freeze({...handle, commandSha256: saved.commandSha256});
    if (!checkHandle(promoted)) return;
    update({handle: promoted, saved, reviewReady: true, checked: false,
      status: 'Исходная команда прочитана. Проверьте результат этой же операции вручную.'});
  });
  const settledBusiness = (result, saved, handle, frame) => {
    if (!validFrame(frame)) return;
    if (result.recoveryState === 'not_found') {
      if (!checkHandle(handle)) return;
      update({submitAllowed: true, reviewReady: true, checked: false, flags: {outcomeUnknown: false, recoveryRequired: false},
        status: 'Результат операции не найден. Повтор возможен только с этой же командой и новым ручным подтверждением.'}); return;
    }
    if (!clearPending(storage(), handle)) {blockStorage(); return;}
    update({handle: null, pendingState: 'none', receipt: result.receipt, evidence: {command: saved.command, receipt: result.receipt},
      submitAllowed: false, checked: false, flags: {outcomeUnknown: false, recoveryRequired: false}, needsFreshCurrent: true,
      status: 'Сервер подтвердил исторический результат. Для новой операции сначала вручную проверьте текущие данные.'});
  };
  const handleRecover = () => {
    if (!originalMatches(viewRef.current || emptyView())) return;
    return run('recover', async frame => {
      const {saved, handle} = viewRef.current; if (!checkHandle(handle)) return;
      const result = await recoverIncomingCommand({operationId: handle.operationId, commandSha256: handle.commandSha256}, {actorId: frame.actorId, saved});
      settledBusiness(result, saved, handle, frame);
    });
  };
  const handleConfirm = () => {
    const state = viewRef.current;
    if (!state || !originalMatches(state) || !state.reviewReady || !state.submitAllowed || state.checked !== true) return;
    const operation = state.saved.operation === 'revision' ? 'revise' : 'confirm';
    return run(operation, async frame => {
      const {saved, handle} = viewRef.current; if (!checkHandle(handle)) return;
      update({checked: false, submitAllowed: false});
      const dispatch = operation === 'revise' ? reviseIncomingCommand : confirmIncomingCommand;
      const result = await dispatch({operationId: handle.operationId, commandSha256: handle.commandSha256, confirm: true}, {actorId: frame.actorId, saved});
      settledBusiness(result, saved, handle, frame);
    });
  };
  const handleHistory = next => run('commands', async frame => {
    const state = viewRef.current, childProductId = state.child === '' ? null : inputId(state.child), sizeId = state.size === '' ? null : inputId(state.size);
    const limit = inputId(state.limit);
    if (state.child !== '' && !childProductId || state.size !== '' && !sizeId || !limit || limit > 100) throw localError('Укажите корректные фильтры и число строк истории от 1 до 100.', 'limit');
    const query = next ? state.pageQuery : {childProductId, sizeId, limit};
    if (!query || next && !state.page?.nextCursor) return;
    const body = {...query, before: next ? state.page.nextCursor : null};
    const page = await discoverIncomingCommands(body, {actorId: frame.actorId});
    if (validFrame(frame)) update({page, pageQuery: query, checked: false, reviewReady: false,
      status: 'Загружена одна страница истории. Выбор строки лишь читает исходную команду.'});
  });

  const alertMessage = actorId === null ? 'Требуется действующий доступ администратора.' : visible.error?.message;
  const errorId = 'incoming-admin-error';
  const fieldError = name => visible.error?.field === name ? {'aria-invalid': true, 'aria-describedby': errorId} : {};
  const canPrepare = controls && !visible.handle && termsReady(visible);
  const canReview = controls && originalMatches(visible) && visible.reviewReady;
  const canConfirm = canReview && visible.submitAllowed && visible.checked;
  const reference = visible.mode === 'revision' ? referenceFor(visible) || '' : visible.terms.reference;
  const draftDisabled = !controls || Boolean(visible.handle);
  const current = visible.current;
  const validateChild = () => {
    if (visible.child !== '' && !inputId(visible.child)) update({error: {message: 'Укажите корректный ID варианта.', field: 'child'}});
  };
  return <section className="incoming-admin" aria-label="Подтверждённые поставки" aria-busy={busy}>
    <h1>Подтверждённые поставки</h1>
    <p>Декларация ответственного сотрудника не является внешней проверкой поставки и не разрешает публичное оформление заказа.</p>
    {alertMessage && <p id={errorId} role="alert" tabIndex={-1} ref={errorRef}>{alertMessage}</p>}
    <p role="status" tabIndex={-1} ref={statusRef}>{busy ? 'Ожидаем ответ сервера.' : visible.status}</p>
    {visible.handle && !visible.saved && <p>Сначала прочитайте сохранённую команду. Новую операцию создавать нельзя.</p>}
    {visible.handle && visible.flags.outcomeUnknown && <p>Результат операции неизвестен. Проверьте результат этой же операции.</p>}
    {visible.handle && visible.flags.recoveryRequired && !visible.flags.outcomeUnknown && <p>Для продолжения проверьте результат этой же операции.</p>}
    <fieldset disabled={!controls}>
      <legend>Выбор SKU и ручная проверка</legend>
      <label htmlFor="incoming-child">ID варианта</label>
      <input id="incoming-child" type="text" inputMode="numeric" autoComplete="off" maxLength={10} value={visible.child}
        {...fieldError('child')} onBlur={validateChild} onChange={event => changeDraft({child: event.target.value, size: '', target: null, current: null, page: null, pageQuery: null})}/>
      <button type="button" onClick={handleTarget}>Загрузить размеры</button>
      <label htmlFor="incoming-size">Размер</label>
      <select id="incoming-size" value={visible.size} {...fieldError('size')}
        onChange={event => changeDraft({size: event.target.value, current: null, page: null, pageQuery: null})}>
        <option value="">Выберите размер</option>
        {visible.target?.candidates.map(candidate => <option key={candidate.sizeId} value={String(candidate.sizeId)}>{candidate.sizeName}</option>)}
      </select>
      <button type="button" disabled={!visible.target || !inputId(visible.size)} onClick={handleCurrent}>Проверить текущие данные</button>
    </fieldset>
    <section aria-label="Текущие данные"><h2>Текущие данные</h2><CurrentDiagnostics current={current}/></section>
    <fieldset disabled={draftDisabled}>
      <legend>Условия поставки</legend>
      <label htmlFor="incoming-mode">Режим операции</label>
      <select id="incoming-mode" disabled={draftDisabled} value={visible.mode} onChange={event => changeDraft({mode: event.target.value, terms: emptyTerms()})}>
        {Object.entries(MODE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
      {visible.mode === 'revision' && !reference && <p>Исходное подтверждение этой поставки не найдено. Изменение пока недоступно.</p>}
      <label htmlFor="incoming-capacity">Всего единиц в подтверждённой поставке</label>
      <input id="incoming-capacity" disabled={draftDisabled} type="number" min="1" max={MAX_ID} step="1" value={visible.terms.capacity} onChange={event => changeTerms('capacity', event.target.value)}/>
      <label htmlFor="incoming-reference">Основание подтверждения</label>
      <input id="incoming-reference" disabled={draftDisabled} type="text" maxLength={128} autoComplete="off" value={reference} readOnly={visible.mode === 'revision'} onChange={event => changeTerms('reference', event.target.value)}/>
      <p>Московское время UTC+03:00</p>
      <label htmlFor="incoming-from">Срок отправки — с (Москва)</label>
      <input id="incoming-from" disabled={draftDisabled} type="text" placeholder="ГГГГ-ММ-ДД ЧЧ:ММ" maxLength={16} value={visible.terms.from} onChange={event => changeTerms('from', event.target.value)}/>
      <label htmlFor="incoming-to">Срок отправки — по (Москва)</label>
      <input id="incoming-to" disabled={draftDisabled} type="text" placeholder="ГГГГ-ММ-ДД ЧЧ:ММ" maxLength={16} value={visible.terms.to} onChange={event => changeTerms('to', event.target.value)}/>
      <label><input disabled={draftDisabled} type="checkbox" checked={visible.terms.payNow} onChange={event => changeTerms('payNow', event.target.checked)}/>Разрешить оплату сейчас</label>
      <label><input disabled={draftDisabled} type="checkbox" checked={visible.terms.payLater} onChange={event => changeTerms('payLater', event.target.checked)}/>Разрешить оплату позже</label>
      {visible.mode === 'revision' && current?.batch && <><p>ID партии: {current.batch.batchId}</p>
        <p>Версия правил: {current.policy.policyRevision}; версия поставки: {current.batch.ledgerRevision}</p></>}
      <button type="button" disabled={!canPrepare} onClick={handlePrepare}>Сохранить команду</button>
    </fieldset>
    <button type="button" disabled={!controls || !visible.handle} onClick={() => handleRead(null)}>Прочитать сохранённую команду</button>
    <section aria-label="Сохранённая команда"><h2>Сохранённая команда</h2>
      {visible.saved ? <OriginalTerms saved={visible.saved}/> : <p>Исходная команда не загружена.</p>}
    </section>
    <fieldset>
      <legend>Подтверждение исходных условий</legend>
      <label><input type="checkbox" checked={visible.checked} disabled={!canReview} onChange={event => update({checked: event.target.checked === true})}/>
        Подтверждаю реальную поставку и эти условия</label>
      <button type="button" disabled={!canConfirm || visible.saved?.operation === 'revision'} onClick={handleConfirm}>Подтвердить поставку</button>
      <button type="button" disabled={!canConfirm || visible.saved?.operation !== 'revision'} onClick={handleConfirm}>Применить изменение</button>
      <button type="button" disabled={!controls || !originalMatches(visible)} onClick={handleRecover}>Проверить результат операции</button>
    </fieldset>
    <section aria-label="Исторический результат"><h2>Исторический результат</h2><HistoricalReceipt receipt={visible.receipt}/></section>
    <fieldset disabled={!controls}>
      <legend>Ручная история сохранённых команд</legend>
      <label htmlFor="incoming-limit">Строк истории на странице</label>
      <input id="incoming-limit" type="number" min="1" max="100" step="1" value={visible.limit} {...fieldError('limit')}
        onChange={event => changeDraft({limit: event.target.value, page: null, pageQuery: null})}/>
      <button type="button" onClick={() => handleHistory(false)}>Загрузить историю команд</button>
      <button type="button" disabled={!visible.page?.nextCursor} onClick={() => handleHistory(true)}>Следующая страница истории</button>
      <p>Сохранение команды не подтверждает поставку. Одна страница не доказывает полноту истории.</p>
      {visible.page && (visible.page.rows.length ? <ul>{visible.page.rows.map(row => <li key={row.operationId}>
        {MODE_LABELS[row.operation]} — {row.operationId}
        <button type="button" disabled={Boolean(visible.handle && (visible.handle.operationId !== row.operationId || visible.handle.commandSha256 !== null && visible.handle.commandSha256 !== row.commandSha256))}
          onClick={() => handleRead(row)}>Прочитать команду {row.operationId}</button>
      </li>)}</ul> : <p>На этой странице нет команд.</p>)}
    </fieldset>
  </section>;
});

export default AdminPreorderIncoming;
